import React, { useMemo, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Divider,
  Modal,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { toast } from "react-toastify";
import api from "../axiosInstance";

function ChatRoom({ openNew, onClose, messages = [] }) {
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);

  const [userInfo] = useState({
    name: "Kondalu",
    phoneNumber: "9654321234",
    senderId: "user123",
  });

  const handleSend = () => {
    if (!input.trim()) return;
    setSending(true);

    const newMsg = {
      id: messages.length + 1,
      senderId: userInfo.senderId,
      senderName: userInfo.name,
      content: input,
      updatedAt: new Date().toISOString(),
    };

    // Normally you'd send to backend here
    setInput("");
    setTimeout(() => setSending(false), 800);
  };

  // Group messages by date
  const groupedMessages = useMemo(() => {
    return messages.reduce((groups, msg) => {
      const date = new Date(msg.updatedAt).toLocaleDateString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
      return groups;
    }, {});
  }, [messages]);

  // Helper: generate consistent color from string (like senderName)
  const getAvatarColor = (name) => {
    const colors = [
      "#4F8EF7",
      "#43A047",
      "#8E24AA",
      "#FB8C00",
      "#E53935",
      "#039BE5",
      "#6D4C41",
      "#3949AB",
      "#00897B",
      "#7B1FA2",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    console.log(id);

    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };

  // Delete The Messages
  const handleDelete = async () => {
    try {
      await api.delete(`/chatroom/messages/${selectedId}`);

      // If deletion is successful, update local state update
      //   ((prev) => prev.filter((row) => row.id !== selectedId));
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  return (
    <>
      <Modal open={openNew} onClose={onClose} disableScrollLock>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            mt: "50px",
            width: { xs: "95%", sm: "60%", md: "40%" },
            mx: "auto",
            borderRadius: 2,
            boxShadow: 3,
            overflow: "hidden",
            bgcolor: "#f9fafc",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: "#17479e",
              color: "white",
              py: 1.5,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {messages[0]?.bookTitle || "Group Chat"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: { md: "20%", xs: "35%" },
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Members: {new Set(messages.map((m) => m.senderId)).size}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  color: "white",
                  ml: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "#ffcccc", // light red on hover
                    backgroundColor: "rgba(255, 255, 255, 0.15)", // subtle background
                  },
                  "&:active": {
                    color: "#f42525ff", // darker red when clicked
                    transform: "scale(0.9)", // slight press effect
                  },
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Messages */}
          <Box
            sx={{
              flexGrow: 1,
              p: 2,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              backgroundImage:
                "linear-gradient(180deg, #ffffff 0%, #f3f6fb 100%)",
            }}
          >
            {Object.keys(groupedMessages).map((date) => (
              <React.Fragment key={date}>
                {/* Date Divider */}
                <Box
                  sx={{
                    textAlign: "center",
                    my: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "inline-block",
                      px: 2,
                      py: 0.4,
                      borderRadius: "8px",
                      backgroundColor: "#f2f5ff",
                      color: "#17479e",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#17479e",
                        color: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {date}
                  </Typography>
                </Box>

                {/* Messages */}
                {groupedMessages[date].map((msg) => {
                  const isMine = msg.senderId === userInfo.senderId;
                  return (
                    <Box
                      key={msg.id}
                      sx={{
                        display: "flex",
                        justifyContent: isMine ? "flex-end" : "flex-start",
                        alignItems: "flex-end",
                        mb: 1.5,
                      }}
                    >
                      {!isMine && (
                        <Avatar
                          sx={{
                            bgcolor: getAvatarColor(msg.senderName),
                            color: "#fff",
                            width: "35px",
                            height: "35px",
                            fontSize: "18px",
                            mr: 1,
                          }}
                        >
                          {msg.senderName[0] || ""}
                        </Avatar>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                          "&:hover .delete-icon": {
                            opacity: 1,
                            transform: "translateX(0)",
                          },
                        }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            p: 1,
                            border: "1px solid #dce7ff",
                            borderRadius: isMine
                              ? "0px 16px 0 16px"
                              : "16px 0px 16px 0",
                            bgcolor: isMine ? "#dce7ff" : "#ffffff",
                            maxWidth: "85%",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {!isMine && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: "#17479e",
                                  display: "block",
                                }}
                              >
                                {msg.senderName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: "#26468299",
                                  display: "block",
                                }}
                              >
                                ~{msg.phoneNumber}
                              </Typography>
                            </Box>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#333",
                              fontSize: "16px",
                              wordBreak: "break-word",
                            }}
                          >
                            {msg.content}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              textAlign: "right",
                              color: "#999",
                              fontSize: "0.7rem",
                              mt: 0.5,
                            }}
                          >
                            {new Date(msg.updatedAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </Paper>

                        {/* Delete Icon (hidden by default) */}
                        <Tooltip title="Delete message" arrow placement="top">
                          <IconButton
                            className="delete-icon"
                            color="error"
                            size="small"
                            sx={{
                              ml: 1,
                              opacity: 0,
                              transform: "translateX(5px)",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "error.light",
                                color: "white",
                              },
                            }}
                            onClick={() => handleClickOpen(msg.id)}
                          >
                            {/* {console.log(messages.id)} */}
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>

          <Divider />

          {/* Input Field */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              bgcolor: "white",
              borderTop: "1px solid #ddd",
            }}
          >
            {/* <TextField
            fullWidth
            size="small"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            sx={{
              mr: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                bgcolor: "#f5f5f5",
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={sending}
            sx={{
              bgcolor: "#17479e",
              color: "white",
              "&:hover": { bgcolor: "#133d8a" },
            }}
          >
            {sending ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <SendIcon />
            )}
          </IconButton> */}
          </Box>
        </Box>
      </Modal>
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}

export default ChatRoom;
